/**
 * Step Executor
 *
 * Executes individual workflow steps with retry logic and error handling.
 *
 * @packageDocumentation
 */

import type { WorkflowStep } from '../types/workflow';
import type { ExecutionContext, StepExecutionLog } from '../types/execution';
import type { AdapterExecutor, AdapterResult } from '../types/adapter';
import { StepExecutionError, TimeoutError } from '../types/errors';
import { resolveStepInput } from './input-resolver';
import { calculateBackoff, sleep, isRecoverableError } from './retry';

// Default timeout for step execution (2 minutes)
// Large models like Claude Opus can take 60+ seconds for complex prompts
const DEFAULT_STEP_TIMEOUT = 120000;

/**
 * Creates a timeout promise that rejects after specified milliseconds
 */
function createTimeout(ms: number, stepName: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new TimeoutError(
        `Step "${stepName}" timed out after ${ms}ms`,
        ms
      ));
    }, ms);
  });
}

/**
 * Executes a single workflow step with retry and fallback logic.
 *
 * This function:
 * 1. Resolves step input (with template interpolation)
 * 2. Calls the adapter executor for primary model
 * 3. Handles retries on recoverable errors
 * 4. Falls back to alternative models if primary fails
 * 5. Creates detailed execution logs
 * 6. Never throws (returns error in log)
 *
 * Fallback behavior: Each model in the chain (primary + fallbacks) gets its own
 * retry attempts. Only after all retries are exhausted does it move to the next model.
 *
 * @param step - The workflow step to execute
 * @param context - Execution context
 * @param adapterExecutor - Function to execute the adapter
 * @returns Step execution log with results or errors
 *
 * @example
 * ```typescript
 * const log = await executeStep(step, context, adapterExecutor);
 *
 * if (log.success) {
 *   console.log('Step succeeded:', log.output);
 * } else {
 *   console.error('Step failed:', log.error);
 * }
 * ```
 */
export async function executeStep(
  step: WorkflowStep,
  context: ExecutionContext,
  adapterExecutor: AdapterExecutor
): Promise<StepExecutionLog> {
  const startTime = Date.now();
  const startedAt = new Date().toISOString();

  // Build model chain: primary + fallbacks
  const modelChain = [step.model, ...(step.fallbackModels || [])];

  // Determine retry configuration
  const maxRetries = step.retry?.maxRetries ?? 0;
  const baseBackoff = step.retry?.backoffMs ?? 1000;
  const timeout = step.timeout ?? DEFAULT_STEP_TIMEOUT;

  let lastError: any;
  let totalAttempts = 0;
  let usedModel = step.model;

  // Try each model in the chain
  for (let modelIndex = 0; modelIndex < modelChain.length; modelIndex++) {
    const currentModel = modelChain[modelIndex];
    usedModel = currentModel;

    // Use provider from metadata if available, otherwise parse from model string
    // Note: Some model names contain colons (e.g., "llama3.2:1b"), so we prefer
    // the explicit provider from metadata when set by the SDK
    const provider = step.metadata?.provider ??
      (currentModel.includes(':') ? currentModel.split(':')[0] : undefined);

    // Build metadata with current provider
    const currentMetadata = {
      ...step.metadata,
      provider,
      // Track fallback usage
      ...(modelIndex > 0 ? {
        fallbackIndex: modelIndex,
        originalModel: step.model,
      } : {}),
    };

    let attempts = 0;

    // Retry loop for current model
    while (attempts <= maxRetries) {
      attempts++;
      totalAttempts++;

      try {
        // Resolve step input
        const resolvedInput = resolveStepInput(step, context);

        // Call adapter with timeout enforcement
        // If provider is from metadata, use model as-is (SDK already strips provider prefix)
        // Otherwise, parse model ID from "provider:model-id" format for backward compatibility
        const modelId = step.metadata?.provider
          ? currentModel
          : (currentModel.includes(':') ? currentModel.split(':')[1] : currentModel);

        const adapterPromise = adapterExecutor({
          model: modelId,
          input: resolvedInput,
          schema: step.schema,
          stepName: step.stepName,
          workflowName: context.workflowName,
          stepMetadata: currentMetadata,
        });

        // Race adapter call against timeout
        const result: AdapterResult = await Promise.race([
          adapterPromise,
          createTimeout(timeout, step.stepName),
        ]);

        // Check if adapter returned success
        if (result.success) {
          const finishedAt = new Date().toISOString();
          const durationMs = Date.now() - startTime;

          // Determine outcome: 'success' if first try, 'recovered' if retry/fallback needed
          const needsRecovery = totalAttempts > 1 || modelIndex > 0;
          const outcome = needsRecovery ? 'recovered' : 'success';

          return {
            stepName: step.stepName,
            model: currentModel,
            startedAt,
            finishedAt,
            durationMs,
            success: true,
            outcome,
            output: result.output,
            attempts: totalAttempts,
            tokensIn: result.tokensIn,
            tokensOut: result.tokensOut,
            // Include fallback metadata if a fallback was used
            ...(modelIndex > 0 ? {
              fallbackUsed: true,
              originalModel: step.model,
              fallbackIndex: modelIndex,
            } : {}),
          };
        }

        // Adapter returned failure
        lastError = result.error;

        // Check if error is recoverable for retry
        if (!isRecoverableError(lastError) || attempts > maxRetries) {
          // Non-recoverable or out of retries for this model
          break;
        }

        // Calculate backoff delay
        const delay = calculateBackoff(attempts - 1, baseBackoff);

        // Wait before retry
        await sleep(delay);
      } catch (error) {
        // Handle timeout errors specially
        if (error instanceof TimeoutError) {
          lastError = {
            type: 'TimeoutError',
            message: error.message,
            timeoutMs: error.timeoutMs,
          };
          // Timeouts exhaust retries for current model, try fallback
          break;
        }

        // Unexpected error from adapter (shouldn't happen, but handle it)
        lastError = {
          type: 'UnexpectedError',
          message: error instanceof Error ? error.message : String(error),
          raw: error,
        };

        // Check if we should retry this model
        if (!isRecoverableError(error) || attempts > maxRetries) {
          break;
        }

        // Calculate backoff delay
        const delay = calculateBackoff(attempts - 1, baseBackoff);

        // Wait before retry
        await sleep(delay);
      }
    }

    // Current model exhausted retries, log and try next fallback
    // (loop continues to next model)
  }

  // All models in chain failed
  const finishedAt = new Date().toISOString();
  const durationMs = Date.now() - startTime;

  return {
    stepName: step.stepName,
    model: usedModel,
    startedAt,
    finishedAt,
    durationMs,
    success: false,
    outcome: 'failed',
    error: lastError || {
      type: 'UnknownError',
      message: 'Step failed with unknown error',
    },
    attempts: totalAttempts,
    // Include info about fallback attempts
    ...(modelChain.length > 1 ? {
      fallbacksAttempted: modelChain.length,
      originalModel: step.model,
    } : {}),
  };
}

/**
 * Validates that a step is ready to execute.
 *
 * @param step - The workflow step
 * @param context - Execution context
 * @throws {StepExecutionError} If step cannot be executed
 */
export function validateStepExecution(step: WorkflowStep, context: ExecutionContext): void {
  // Check required fields
  if (!step.stepName || !step.model) {
    throw new StepExecutionError(
      step.stepName || 'unknown',
      context.workflowName,
      'Step is missing required fields (stepName or model)',
      0
    );
  }

  // Check dependencies are met
  if (step.dependsOn && step.dependsOn.length > 0) {
    for (const depName of step.dependsOn) {
      if (!context.hasOutput(depName)) {
        throw new StepExecutionError(
          step.stepName,
          context.workflowName,
          `Step "${step.stepName}" depends on "${depName}" which has not been executed`,
          0
        );
      }
    }
  }
}
